'use client'
import {
  cartData,
  checkoutData,
  productData,
  purchaseData,
  subscriptionPurchaseData,
  userData
} from "./chunk-XEYR46V6.mjs";
import {
  CHECKOUT_DATA_COOKIE_NAME,
  CONSENT_COOKIE_NAME,
  USER_DATA_STORAGE_KEY
} from "./chunk-T2NN7R53.mjs";
import {
  __async,
  __spreadProps,
  __spreadValues
} from "./chunk-UYSR6ZT4.mjs";

// src/trackers/gtm/index.ts
import {
  getItem,
  loadScriptAsync,
  removeItem,
  runWhenIdle,
  safeParseJson,
  setItem
} from "@wbk/utils";
var currentPath = () => typeof window === "undefined" ? void 0 : window.location.pathname;
function gtag(..._args) {
  if (!window.dataLayer) window.dataLayer = [];
  window.dataLayer.push(arguments);
}
var GtmTracker = class {
  constructor({ gtmIds, log }) {
    this.device = "web";
    /** Events raised while the container fetch is deferred; replayed after its `gtm.js` message */
    this.pending = null;
    /**
     * Init GTM script and set default cookie consent
     * @returns void
     */
    this.initGTM = () => {
      const canRun = typeof window !== "undefined" && !!this.gtmIds.length;
      if (!canRun) return;
      const consent = getItem(CONSENT_COOKIE_NAME);
      if (consent) {
        const parsed = safeParseJson(consent);
        this.setCookieConsent({ type: "default", data: parsed });
      } else {
        const search = new URLSearchParams(window.location.search);
        const hasGl = search.has("_gl");
        const data = {
          analytics_storage: hasGl ? "granted" : "denied",
          ad_storage: hasGl ? "granted" : "denied",
          ad_user_data: hasGl ? "granted" : "denied",
          ad_personalization: hasGl ? "granted" : "denied",
          date: (/* @__PURE__ */ new Date()).toISOString()
        };
        if (hasGl) {
          setItem(CONSENT_COOKIE_NAME, JSON.stringify(data));
        }
        this.setCookieConsent({
          type: "default",
          data
        });
      }
      const ids = this.gtmIds.filter((id) => !!id.trim());
      if (!ids.length) return;
      this.pending = [];
      runWhenIdle(() => this.loadContainers(ids));
    };
    this.loadContainers = (ids) => {
      for (const id of ids) {
        if (!id) continue;
        loadScriptAsync({
          id,
          target: "head",
          snippet: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                    })(window,document,'script','dataLayer','${id}');`
        });
        loadScriptAsync({
          id: `${id}_noscript`,
          type: "noscript",
          snippet: `<iframe src="https://www.googletagmanager.com/ns.html?id=${id}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`
        });
      }
      const queued = this.pending;
      this.pending = null;
      queued == null ? void 0 : queued.forEach((event) => {
        var _a;
        return (_a = window.dataLayer) == null ? void 0 : _a.push(event);
      });
    };
    /**
     * Set device type
     */
    this.setDevice = (device) => {
      this.device = device;
    };
    /**
     * GTM consent mode guide
     * https://developers.google.com/tag-platform/security/guides/consent
     */
    this.setCookieConsent = ({ type, data }) => {
      const cloned = __spreadProps(__spreadValues({}, data), { date: void 0 });
      delete cloned.date;
      gtag("consent", type, cloned);
    };
    this.pageView = () => {
    };
    this.signupEvent = (_0) => __async(this, [_0], function* ({ method, user }) {
      const data = yield userData(user);
      this.pushToDataLayer({
        event: "signup",
        method,
        user_data: {
          data: [data]
        }
      });
    });
    this.loginEvent = (_0) => __async(this, [_0], function* ({
      method,
      user,
      subscriptions
    }) {
      const data = yield userData(user, subscriptions);
      this.pushToDataLayer({
        event: "login",
        method,
        user_data: {
          data: [data]
        }
      });
    });
    this.setUser = (user) => {
      this.pushToDataLayer({
        user_data: { data: [__spreadProps(__spreadValues({}, user), { access_token: "" })] }
      });
    };
    this.logoutEvent = () => {
      this.pushToDataLayer({ event: "logout" });
    };
    this.viewItemEvent = (props) => __async(this, null, function* () {
      const userDataString = getItem(USER_DATA_STORAGE_KEY) || "";
      const userData2 = safeParseJson(userDataString);
      const data = productData(props);
      this.pushToDataLayer({ ecommerce: null });
      this.pushToDataLayer({
        event: "view_item",
        ecommerce: data,
        user_data: {
          data: [userData2]
        },
        booking_type: props.booking_type
      });
    });
    this.viewItemListEvent = ({ events, listId = "", listName = "" }) => {
      this.pushToDataLayer({ ecommerce: null });
      this.pushToDataLayer({
        event: "view_item_list",
        ecommerce: {
          item_list_id: listId,
          item_list_name: listName,
          items: events.map((event) => ({
            item_id: event._id,
            item_name: event.title,
            item_category: event.category || "N/A",
            country: event.country || ""
          }))
        }
      });
    };
    this.generateLeadEvent = (event) => {
      this.pushToDataLayer({
        event: "generate_lead",
        currency: event.currency,
        value: event.event_start_price,
        slug: event.slug,
        name: event.title,
        booking_type: event.booking_type
      });
    };
    this.viewItemBook = (props) => {
      var _a;
      const userDataString = getItem(USER_DATA_STORAGE_KEY) || "";
      const userData2 = safeParseJson(userDataString);
      const data = productData(props);
      this.pushToDataLayer({ ecommerce: null });
      this.pushToDataLayer({
        event: "view_item_book",
        ecommerce: data,
        booking_type: (_a = props.event) == null ? void 0 : _a.contentful_entity_type,
        user_data: {
          data: [userData2]
        }
      });
    };
    this.cartEvent = (props) => {
      var _a;
      const data = cartData(props);
      this.pushToDataLayer({ ecommerce: null });
      const event = props.type === "add" ? "add_to_cart" : "remove_from_cart";
      this.pushToDataLayer({
        event,
        ecommerce: data.order,
        user_data: {
          data: [data.userData]
        },
        booking_type: (_a = props.event) == null ? void 0 : _a.booking_type
      });
    };
    this.viewSummaryEvent = (props) => {
      var _a;
      const data = checkoutData(props);
      this.pushToDataLayer({ ecommerce: null });
      this.pushToDataLayer({
        event: "add_payment_info",
        ecommerce: data.order,
        user_data: {
          data: [data.userData]
        },
        booking_type: (_a = props.event) == null ? void 0 : _a.booking_type
      });
    };
    this.ticketsSummaryEvent = () => {
    };
    this.paymentSummaryEvent = () => {
    };
    this.beginCheckout = (props) => {
      var _a;
      const data = checkoutData(props);
      this.pushToDataLayer({ ecommerce: null });
      this.pushToDataLayer({
        event: props.checkout_event_name || "begin_checkout",
        ecommerce: data.order,
        payment_method: props.payment_method,
        user_data: {
          data: [data.userData]
        },
        booking_type: (_a = props.event) == null ? void 0 : _a.booking_type
      });
    };
    this.purchaseEvent = (payment) => __async(this, null, function* () {
      const data = purchaseData(payment);
      this.pushToDataLayer({ ecommerce: null });
      this.pushToDataLayer({
        event: "purchase",
        ecommerce: data.order,
        user_data: {
          data: [data.userData]
        },
        booking_type: payment.contentful_entity_type
      });
      removeItem(CHECKOUT_DATA_COOKIE_NAME);
    });
    this.purchaseSubscriptionEvent = (props) => {
      const data = subscriptionPurchaseData(props);
      this.pushToDataLayer({ ecommerce: null });
      this.pushToDataLayer({
        event: "purchase",
        ecommerce: data.order,
        user_data: {
          data: [data.userData]
        },
        booking_type: "subscription"
      });
      removeItem(CHECKOUT_DATA_COOKIE_NAME);
    };
    this.purchaseFailed = (props) => {
      const reason = props == null ? void 0 : props.reason;
      this.pushToDataLayer({
        event: "purchase_failed",
        reason,
        booking_type: (props == null ? void 0 : props.booking_type) || ""
      });
    };
    this.shareEvent = ({ method, id, contentType }) => {
      this.pushToDataLayer({
        event: "share",
        method,
        id,
        content_type: contentType
      });
    };
    this.registerInterestEvent = (data) => {
      this.pushToDataLayer(__spreadValues({
        event: "notify_me"
      }, data));
    };
    this.newsletterEvent = (data) => {
      this.pushToDataLayer(__spreadValues({
        event: "subscribe_newsletter"
      }, data));
    };
    this.trackEvent = (props) => {
      this.pushToDataLayer(__spreadValues({ event: props.event, page: currentPath() }, props.data));
    };
    this.clearCart = () => {
    };
    /**
     * Unified function to push data to dataLayer
     */
    this.pushToDataLayer = (obj) => {
      if (typeof window !== "undefined") {
        const dataLayer = window == null ? void 0 : window.dataLayer;
        if (dataLayer) {
          const event = __spreadProps(__spreadValues({}, obj), { device: this.device });
          if (this.pending) {
            this.pending.push(event);
          } else {
            dataLayer.push(event);
          }
          if (this.log) {
            console.log("Pushed to dataLayer:", obj);
          }
        }
      }
    };
    this.gtmIds = gtmIds;
    this.log = !!log;
    this.initGTM();
  }
};

export {
  GtmTracker
};
//# sourceMappingURL=chunk-7OJ3XMT7.mjs.map